import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeftRight,
  BookOpenCheck,
  FolderDown,
  GraduationCap,
  LayoutDashboard,
  LogOut,
  Menu,
  Rocket,
  Settings,
  ShieldCheck,
  X,
} from 'lucide-react';

import { BrandLink } from '@/components/Brand';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useAuth } from '@/features/auth/AuthContext';
import { fetchers, queryKeys } from '@/services/queries';
import { cn } from '@/lib/cn';
import { initials, roleLabel } from '@/lib/format';
import type { UserRole } from '@/types/api';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  roles?: UserRole[];
}

const NAV: NavItem[] = [
  { to: '/app', label: 'Tableau de bord', icon: LayoutDashboard },
  { to: '/app/formations', label: 'Mes formations', icon: GraduationCap },
  { to: '/app/ressources', label: 'Ressources', icon: FolderDown },
  { to: '/projets', label: 'Projets fil rouge', icon: Rocket },
  { to: '/app/formateur', label: 'Espace formateur', icon: BookOpenCheck, roles: ['instructor', 'admin'] },
  { to: '/app/administration', label: 'Administration', icon: Settings, roles: ['admin'] },
];

export function AppLayout() {
  const { user, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // L'adresse d'origine vient du serveur, pas du bundle : déplacer EFILearn ne
  // demande donc pas de reconstruire le frontend. Vide = aucun retour à offrir,
  // et le bouton disparaît de lui-même.
  const providers = useQuery({ queryKey: queryKeys.providers, queryFn: fetchers.providers });
  const retourUrl = providers.data?.federation_origin_url?.trim();

  // Items are filtered for readability only — each route is also guarded, and
  // the API re-checks every permission.
  const items = NAV.filter((item) => !item.roles || (user && item.roles.includes(user.role)));

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-bg">
      <a href="#contenu" className="skip-link">
        Aller au contenu principal
      </a>

      {/* Sidebar */}
      <aside
        className={cn(
          'kha-no-print fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-border',
          'bg-surface transition-transform lg:static lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-border px-4">
          <BrandLink />
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setOpen(false)}
            aria-label="Fermer le menu"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <nav aria-label="Navigation de l'espace" className="flex-1 overflow-y-auto p-3">
          <ul className="space-y-1">
            {items.map(({ to, label, icon: Icon }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  end={to === '/app'}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-tech/12 text-tech ring-1 ring-inset ring-tech/20'
                        : 'text-muted hover:bg-surface-2 hover:text-fg',
                    )
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {user ? (
          <div className="shrink-0 border-t border-border p-3">
            <div className="flex items-center gap-3 rounded-lg bg-surface-2 p-3">
              <span
                className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gradient-to-br
                           from-tech to-ai text-xs font-bold text-white"
                aria-hidden="true"
              >
                {initials(user.full_name)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-fg">{user.full_name}</p>
                <p className="truncate text-2xs text-faint">{user.email}</p>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-between gap-2">
              <Badge tone={hasRole('admin') ? 'ai' : hasRole('instructor') ? 'tech' : 'neutral'}>
                <ShieldCheck className="h-3 w-3" />
                {roleLabel(user.role)}
              </Badge>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="h-3.5 w-3.5" />
                Quitter
              </Button>
            </div>
          </div>
        ) : null}
      </aside>

      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="kha-no-print sticky top-0 z-30 flex h-16 items-center gap-3 border-b
                           border-border bg-bg/85 px-4 backdrop-blur-lg lg:px-8">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setOpen(true)}
            aria-label="Ouvrir le menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="ml-auto flex items-center gap-2">
            {retourUrl ? (
              <a
                href={retourUrl}
                className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5
                           text-sm font-medium text-muted transition-colors
                           hover:bg-surface-2 hover:text-fg"
                title="Revenir à la plateforme EFILearn"
              >
                <ArrowLeftRight className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="hidden sm:inline">Retour à EFILearn</span>
                <span className="sr-only sm:hidden">Retour à EFILearn</span>
              </a>
            ) : null}
            <ThemeToggle />
          </div>
        </header>

        <main id="contenu" className="min-w-0 flex-1 px-4 py-6 lg:px-8 lg:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
